import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { ToastProvider } from '@core';
import { InvestigationsRepository } from '../../../infrastructure/repositories/investigations.repository';

export interface ReopenInvestigationCommand {
  readonly investigationId: string;
  readonly investigationNumber: string; // Display value (e.g. "ER-26060001")
  readonly reopeningReasonId: string;
  readonly reopeningNotes: string;
}

export interface ReopenInvestigationResult {
  readonly success: boolean;
  readonly investigationId: string;
}

/**
 * Reopen Investigation Use Case
 *
 * Reopens a closed investigation with a mandatory reopening reason and notes.
 * The server resets the investigation to the "New" status, clears assignees, and
 * re-adds it to the Intake Queue for reassignment.
 */
@Injectable({ providedIn: 'root' })
export class ReopenInvestigationUseCase {
  private readonly _repository = inject(InvestigationsRepository);
  private readonly _toast = inject(ToastProvider);

  public execute(command: ReopenInvestigationCommand): Observable<ReopenInvestigationResult> {
    return this._repository
      .reopenInvestigation(command.investigationId, command.reopeningReasonId, command.reopeningNotes)
      .pipe(
        tap(() => {
          this._toast.success(`Reopen request for ${command.investigationNumber} submitted successfully`);
        }),
        map(() => ({ success: true, investigationId: command.investigationId })),
        catchError((error) => {
          this._toast.error('Failed to reopen investigation', 'Please try again or contact support.');
          return throwError(() => error);
        }),
      );
  }
}
